import type { ReactNode } from 'react';
import { Badge, Table, cn, type Column, type TableProps } from '@eombor/ui';
import { DESKTOP_QUERY, useMediaQuery } from './useMediaQuery.js';

/**
 * Ustun kartochkada QAYERDA turishi.
 *
 * Kalitlar `columns` dagi `key` lar bilan bir xil. Bu yerda nomlanmagan
 * ustunlarning hammasi «yorliq: qiymat» qatoriga tushadi — ya'ni yangi
 * ustun qo'shilganda u kartochkada O'ZI paydo bo'ladi va tushib qolmaydi.
 */
export interface CardLayout<T> {
  /** Tepadagi kichik identifikator (kod, hujjat raqami) — monospace. */
  code?: string;
  /** Kartochkaning eng ko'zga tashlanadigan matni. */
  title: string;
  /**
   * Sarlavhani kartochkada BOSHQACHA chizish.
   *
   * Kerak bo'ladigan sabab aniq: jadval katagida uzun nom `truncate`
   * bilan bir qatorga kesiladi — u yerda qator balandligi qat'iy.
   * Kartochkada esa aksincha, nom to'liq o'qilishi kerak va ikki
   * qatorga o'ralishi mumkin. Berilmasa ustunning o'z `render` i
   * ishlatiladi.
   */
  renderTitle?: (row: T) => ReactNode;
  /** Amallar ustuni — yorliqsiz, kartochka ostida alohida qatorda. */
  actions?: string;
  /**
   * Amallarni kartochkada BOSHQACHA chizish.
   *
   * `renderTitle` bilan bir sabab: jadval katagida tugma kichik
   * (`sm`, 36px) va o'ngga tekislangan bo'ladi — u yerda qator baland
   * emas va tugma ustunga sig'ishi kerak. Kartochkada esa aksincha,
   * asosiy amal butun enni oladi va barmoq nishoni 44px bo'ladi.
   *
   * Berilsa, quti o'ngga tekislanmaydi — ichki joylashuvni to'liq
   * chaqiruvchi hal qiladi.
   */
  renderActions?: (row: T) => ReactNode;
  /**
   * Tanlash katakchasi turgan ustun — kartochkaning yuqori chap
   * burchagida, tartib raqamdan oldin.
   *
   * Ustunning O'Z `render` i ishlatiladi: ko'p tanlash mantig'i
   * jadvalda ham, kartochkada ham AYNI bitta joyda qoladi va ikkiga
   * bo'linib ketmaydi.
   */
  select?: string;
  /**
   * Tartib raqam — ro'yxatdagi KO'RINISH tartibi bo'yicha, 1 dan.
   *
   * Ma'lumotdagi identifikator emas: filtr yoki qidiruvdan keyin
   * ro'yxat qisqaradi va odam «uchinchisi» deganda ekranda uchinchi
   * turganini nazarda tutadi.
   */
  numbered?: boolean;
  /**
   * Tartib raqam turgan USTUN — `numbered` o'rniga.
   *
   * Ba'zi ro'yxatda raqam qatorning o'z xossasi bo'ladi (hujjatdagi
   * satr raqami, fayldagi qator). Bunday raqam filtr, qidiruv va
   * qayta saralashdan keyin ham O'ZGARMASLIGI kerak: skladchi uni
   * qog'ozdagi hujjat bilan solishtiradi, ekrandagi o'rni bilan emas.
   *
   * `code`/`title`/`actions` bilan bir xil qoida: ustun kartochkada
   * O'Z o'rniga (nishon ichiga) chiqadi va maydonlar setkasida
   * ikkinchi marta takrorlanmaydi.
   *
   * Berilsa `numbered` e'tiborga olinmaydi.
   */
  number?: string;
  /**
   * Chip ko'rinishida chiziladigan ustunlar.
   *
   * Faqat bir qarashda o'qiladigan QISQA qiymatlar uchun (birlik,
   * chegara): ular ro'yxatda ko'z yuguradigan joy bo'ladi. Uzun matnni
   * chipga solish uni o'qib bo'lmas holga keltiradi.
   */
  chips?: readonly string[];
  /** Setkada ikkala ustunni egallaydigan (uzun qiymatli) ustunlar. */
  wide?: readonly string[];
  /**
   * AJRATIB ko'rsatiladigan ustunlar — yumshoq fonli alohida blokda va
   * setkaning eng boshida.
   *
   * Kartochkada hamma maydon bir xil vaznda turadi va bu odatda to'g'ri.
   * Lekin ba'zi ro'yxatda bitta maydon qaror qabul qilishda qolganidan
   * muhimroq bo'ladi (tushum hujjatida — kirim sanasi: skladchi aynan
   * shunga qarab qaysi partiyani oldin joylashtirishni hal qiladi).
   * Bir tekis setkada u qo'shni maydonlar orasida ko'zdan qochardi.
   *
   * Blok TEPADA turadi: ustunlar tartibida u o'rtada bo'lishi mumkin,
   * ammo kartochkada muhim maydon uchun aylantirish kerak bo'lmasin.
   */
  highlight?: readonly string[];
  /**
   * Ustunlar orasida YO'Q, lekin ma'lumotda bor maydonlar.
   *
   * Jadval hamma narsani ustunga chiqarmaydi — joy cheklangan. Kartochkada
   * esa cheklov yo'q va maydon tushib qolmasligi kerak.
   */
  extra?: readonly CardExtraField<T>[];
}

/** Ustunga aylanmagan, lekin kartochkada ko'rinadigan maydon. */
export interface CardExtraField<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  wide?: boolean;
  chip?: boolean;
  highlight?: boolean;
}

export interface ResponsiveTableProps<T> extends TableProps<T> {
  /**
   * Telefonda kartochka qanday yig'ilishi.
   *
   * Berilmasa komponent oddiy `Table` bo'lib qoladi — bu ataylab:
   * jadvalni kartochkaga o'tkazish HAR DOIM ham to'g'ri emas va qaror
   * chaqiruvchida qolishi kerak.
   */
  card?: CardLayout<T>;
}

/** `Table` ning o'z qoidasi bilan bir xil: bo'sh qiymat chizilmaydi. */
const toCell = (value: unknown): ReactNode => {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'string' || typeof value === 'number') return value;
  return String(value);
};

const cellOf = <T,>(column: Column<T>, row: T): ReactNode =>
  column.render
    ? column.render(row)
    : toCell((row as unknown as Record<string, unknown>)[column.key]);

/**
 * Qiymat BO'SHmi.
 *
 * `render` ixtiyoriy tugun qaytaradi va uni chizmasdan turib bilib
 * bo'lmaydi — shuning uchun faqat aniq bo'sh belgilar tekshiriladi:
 * `null`, `undefined` va bo'sh satr. Qolgani bor deb hisoblanadi.
 */
const isBlank = (value: ReactNode): boolean =>
  value === null || value === undefined || value === '' || value === false;

/** Bo'sh maydon o'rnida — «—»: yorliq qoladi, javob yo'qligi ko'rinadi. */
const DASH = '—';

/** Kartochkadagi bitta maydon: yorliq tepada, qiymat ostida. */
interface CardField {
  key: string;
  label: string;
  value: ReactNode;
  wide: boolean;
  chip: boolean;
  mono: boolean;
  highlight: boolean;
}

const Card = <T,>({
  row,
  position,
  rowId,
  columns,
  card,
  onRowClick,
  className,
}: {
  row: T;
  position: number;
  rowId: string;
  columns: Column<T>[];
  card: CardLayout<T>;
  onRowClick?: (row: T) => void;
  className?: string;
}) => {
  const byKey = (key: string | undefined): Column<T> | undefined =>
    key === undefined ? undefined : columns.find((column) => column.key === key);

  const codeColumn = byKey(card.code);
  const titleColumn = byKey(card.title);
  const actionsColumn = byKey(card.actions);
  const selectColumn = byKey(card.select);
  const numberColumn = byKey(card.number);

  /* Qolgan HAMMASI — tartibi ustunlar ro'yxatidagidek. Ro'yxatga qo'lda
     yozilmaydi: yangi ustun qo'shilsa u kartochkada o'zi paydo bo'ladi. */
  const fields: CardField[] = [
    ...columns
      .filter(
        (column) =>
          column !== codeColumn &&
          column !== titleColumn &&
          column !== actionsColumn &&
          column !== selectColumn &&
          column !== numberColumn,
      )
      .map((column) => ({
        key: column.key,
        label: column.header,
        value: cellOf(column, row),
        wide: card.wide?.includes(column.key) ?? false,
        chip: card.chips?.includes(column.key) ?? false,
        mono: column.mono === true,
        highlight: card.highlight?.includes(column.key) ?? false,
      })),
    ...(card.extra ?? []).map((field) => ({
      key: field.key,
      label: field.label,
      value: field.render(row),
      wide: field.wide ?? false,
      chip: field.chip ?? false,
      mono: false,
      highlight: field.highlight ?? false,
    })),
  ]
    /* Ajratilganlar TEPAGA. Saralash barqaror (`sort` ES2019 dan beri
       shunday), ya'ni qolgan maydonlar ustunlar tartibini saqlaydi —
       faqat urg'ulanganlari oldinga suriladi. */
    .sort((a, b) => Number(b.highlight) - Number(a.highlight));

  const clickable = typeof onRowClick === 'function';
  const titleNode =
    card.renderTitle?.(row) ?? cellOf(titleColumn ?? (columns[0] as Column<T>), row);

  /* Tanlash katakchasi HAR qatorda bo'lavermaydi: ustunning `render` i
     ish qolmagan satrda `null` qaytaradi. Bo'sh nishonni chizish nom va
     raqamni bekorga o'ngga surib qo'yardi. */
  const selectNode = selectColumn === undefined ? null : cellOf(selectColumn, row);
  const hasSelect = !isBlank(selectNode);

  const numberNode =
    numberColumn === undefined
      ? card.numbered === true
        ? position
        : null
      : cellOf(numberColumn, row);

  const actionsNode =
    card.renderActions?.(row) ??
    (actionsColumn === undefined ? null : cellOf(actionsColumn, row));

  return (
    <li
      // FLIP animatsiyasi kartochkani ham shu belgi bo'yicha topadi —
      // qator tepaga ko'chganda telefonda ham siljish ko'rinadi.
      data-row-key={rowId}
      className={cn(
        'relative flex flex-col gap-3 rounded-[var(--radius-lg)] border border-border',
        'bg-surface p-4 shadow-[var(--shadow-1)]',
        clickable
          ? cn(
              'transition-[background-color,border-color,transform]',
              'duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
              'hover:border-accent/30 hover:bg-accent-soft/40',
              // Bosilganda yengil cho'kish — barmoq tegganini KO'RADI.
              'has-[button:active]:translate-y-px has-[button:active]:bg-accent-soft/60',
              'motion-reduce:transition-none motion-reduce:has-[button:active]:translate-y-0',
            )
          : undefined,
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        {!hasSelect ? null : (
          /*
            TANLASH — eng chapda, raqamdan oldin.
            `z-[1]`: bosiladigan kartochkada nom tugmasining qoplamasi
            butun yuzani egallaydi va usiz katakcha bosilmasdi — bosish
            qatorni ochib yuborardi. Quti 44px, katakcha esa kattaroq
            chiziladi: jadvaldagi 13px teginish uchun kichik.
          */
          <span
            className={cn(
              'relative z-[1] -my-1.5 -ml-1.5 flex size-11 shrink-0',
              'items-center justify-center',
              '[&_input]:size-[1.125rem] [&_input]:cursor-pointer',
            )}
          >
            {selectNode}
          </span>
        )}

        {isBlank(numberNode) ? null : (
          /* Tartib raqam SUSROQ: u yo'nalish beradi, lekin nomdan
             ko'zni tortib olmasligi kerak. `min-w`, `size` emas —
             hujjatdagi satr raqami uch xonali ham bo'ladi va doira
             ichida kesilib qolmasligi kerak. */
          <span
            aria-hidden
            className={cn(
              'tabular mt-0.5 flex h-7 min-w-7 shrink-0 items-center justify-center px-1.5',
              'rounded-[var(--radius-pill)] bg-surface-2 text-sm font-semibold text-text-faint',
            )}
          >
            {numberNode}
          </span>
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          {/*
            NOM — eng katta va eng to'q element, birinchi ko'zga tashlanadi.
            Bosiladigan jadvalda u tugma bo'ladi va uning `::after` i butun
            kartochkani qoplaydi: shunda amallar tugmalari bilan ichma-ich
            tugma hosil bo'lmaydi, yuza esa baribir butunlay bosiladi.

            Uzun nom KESILMAYDI — ikki qatorga o'raladi: telefonda o'ngda
            joy yo'q va «Shpal beton B-25…» degan yozuv hech narsa aytmaydi.

            O'lcham `text-lg` — kartochkadagi eng katta matn. Qator
            balandligi shkalaning O'ZIDAN keladi (`--text-lg--line-height`),
            shuning uchun `leading-*` qo'lda yozilmaydi: aks holda shrift
            kattalashgach ikki qator bir-biriga yopishib qolardi.
          */}
          {clickable ? (
            <button
              type="button"
              onClick={() => onRowClick(row)}
              className={cn(
                'line-clamp-2 text-left text-lg font-semibold text-pretty text-text',
                "after:absolute after:inset-0 after:content-['']",
                'after:rounded-[var(--radius-lg)]',
              )}
            >
              {titleNode}
            </button>
          ) : (
            <span className="line-clamp-2 text-lg font-semibold text-pretty text-text">
              {titleNode}
            </span>
          )}

          {/* KOD — chip ichida: u identifikator, ya'ni nomdan boshqa
              turdagi ma'lumot va shu bilan ajralib turishi kerak. */}
          {codeColumn === undefined ? null : (
            <span className="flex">
              <span
                className={cn(
                  'tabular inline-flex items-center rounded-[var(--radius-sm)]',
                  'bg-surface-2 px-2 py-1 text-sm font-medium text-text-muted',
                )}
              >
                {cellOf(codeColumn, row)}
              </span>
            </span>
          )}
        </div>
      </div>

      {fields.length === 0 ? null : (
        /*
          TELEFONDA bitta ustun, planshetdan boshlab ikkita.
          Ikki ustun vertikal ro'yxatning cho'zilib ketishidan saqlaydi,
          lekin 15px qiymat va 13px yorliq uchun telefonning yarim eni
          juda tor: sana ikki qatorga sinib, son o'z yorlig'idan uzoqlashib
          ketardi. Uzun qiymat esa `wide` bilan ikkalasini ham egallaydi —
          bitta ustunli holatda u allaqachon to'liq en, shuning uchun
          `col-span-2` faqat `sm:` dan boshlanadi (aks holda bir ustunli
          setkada ikkinchi, ko'rinmas ustun paydo bo'lardi).
        */
        <dl className="grid grid-cols-1 gap-x-4 gap-y-3 border-t border-border pt-3 sm:grid-cols-2">
          {fields.map((field) =>
            field.highlight ? (
              /*
                AJRATILGAN maydon — yumshoq fonli blok, setkaning boshida
                va butun enni egallagan holda. Fon `surface-2`: palitradagi
                mavjud "ikkinchi yuza", ya'ni yangi rang o'ylab topilmaydi
                va u qorong'i temada ham o'zi to'g'rilanadi.
              */
              <div
                key={field.key}
                className={cn(
                  'flex min-w-0 flex-col gap-0.5 sm:col-span-2',
                  'rounded-[var(--radius-sm)] bg-surface-2 px-3 py-2',
                )}
              >
                <dt className="text-sm text-text-muted">{field.label}</dt>
                <dd
                  className={cn(
                    'min-w-0 text-base font-semibold break-words text-text',
                    field.mono ? 'tabular' : undefined,
                  )}
                >
                  {isBlank(field.value) ? (
                    <span className="font-normal text-text-faint">{DASH}</span>
                  ) : (
                    field.value
                  )}
                </dd>
              </div>
            ) : (
              <div
                key={field.key}
                className={cn(
                  'flex min-w-0 flex-col gap-0.5',
                  field.wide ? 'sm:col-span-2' : undefined,
                )}
              >
                {/* Yorliq qiymatdan KICHIKROQ, lekin o'qilarli: 13px —
                    shkalaning eng kichik "matn" pog'onasi. Qator
                    balandliklari shkaladan keladi, qo'lda yozilmaydi. */}
                <dt className="text-sm text-text-faint">{field.label}</dt>
                <dd className="min-w-0 text-base break-words text-text">
                  {isBlank(field.value) ? (
                    <span className="text-text-faint">{DASH}</span>
                  ) : field.chip ? (
                    /* Nishonning o'z o'lchami 12px — kartochkada u
                       qolgan qiymatlardan mayda bo'lib qolardi. `!`
                       kerak: `cn` — oddiy birlashtiruvchi. */
                    <Badge tone="neutral" mono={field.mono} className="text-sm!">
                      {field.value}
                    </Badge>
                  ) : (
                    <span className={cn(field.mono ? 'tabular' : undefined)}>{field.value}</span>
                  )}
                </dd>
              </div>
            ),
          )}
        </dl>
      )}

      {isBlank(actionsNode) ? null : (
        /* Amallar nom tugmasining qoplamasi USTIDA — aks holda tahrir
           tugmasi bosilganda qator ochilishi ham ishga tushardi.

           `renderActions` berilgan bo'lsa o'ngga tekislanmaydi: u yerda
           asosiy amal butun enni oladi va `justify-end` uni qisqartirib
           qo'yardi. */
        <div
          className={cn(
            'relative z-[1] flex border-t border-border pt-3',
            card.renderActions === undefined ? 'justify-end' : 'flex-col',
          )}
        >
          {actionsNode}
        </div>
      )}
    </li>
  );
};

/**
 * KENG ekranda jadval, telefonda kartochkalar — bitta ustunlar ta'rifidan.
 *
 * Nega bitta komponent: ustunlar ro'yxati, sarlavhalar va `render`
 * funksiyalari BIR joyda qoladi. Ikki alohida ko'rinish yozilsa, ular
 * asta-sekin farq qila boshlaydi — bir yerda yangilangan formatlash
 * ikkinchisiga yetib bormaydi va yangi ustun kartochkada tushib qoladi.
 *
 * Tanlov CSS emas, JS (`useMediaQuery`): ikkalasini ham chizib, birini
 * `hidden` bilan yashirish ekran o'qigichga AYNI ro'yxatni ikki marta
 * berardi. Ilovada bu qoida bitta (`useMobileNav` da ham shunday).
 */
export const ResponsiveTable = <T,>({ card, ...table }: ResponsiveTableProps<T>) => {
  const mobile = !useMediaQuery(DESKTOP_QUERY);

  if (!mobile || card === undefined) return <Table {...table} />;

  const { columns, rowKey, empty, onRowClick, caption, rowClassName, groups } = table;
  /*
   * `rows` turi bo'yicha massiv, lekin ma'lumot hali kelmagan joyda
   * chaqiruvchi `data?.items` ni to'g'ridan-to'g'ri uzatib yuborishi
   * mumkin. Bunda `undefined.map()` butun sahifani yiqitardi — oq
   * ekran esa xatoning eng qimmat turi.
   */
  const rows = table.rows ?? [];

  if (rows.length === 0) {
    return empty === undefined ? null : (
      <div
        className={cn(
          'rounded-[var(--radius-lg)] border border-border bg-surface',
          'p-4 shadow-[var(--shadow-1)]',
        )}
      >
        {empty}
      </div>
    );
  }

  const cardOf = (row: T, position: number, extraClassName?: string) => {
    /* Qator sinflari IKKI manbadan: jadvalning `rowClassName` i (belgi,
       urg'u) va bo'limning o'ziniki (joylashtirilgan satrlar foni). */
    const className = cn(rowClassName?.(row), extraClassName);
    return (
      <Card
        key={rowKey(row)}
        row={row}
        rowId={rowKey(row)}
        position={position}
        columns={columns}
        card={card}
        {...(onRowClick === undefined ? {} : { onRowClick })}
        {...(className === '' ? {} : { className })}
      />
    );
  };

  /*
   * BO'LIMLAR kartochkada ham saqlanadi.
   *
   * Jadvaldagi bo'lim sarlavhasi ("Joylashtirilgan 4", "Qolgani 12")
   * shunchaki bezak emas — u qatorlarning MA'NOSINI aytadi. Telefonda
   * uni tashlab ketish ro'yxatni bir tekis yassi qilib qo'yardi va
   * odam nima qilib bo'lganini nima qolganidan ajrata olmasdi.
   *
   * Raqamlash bo'limlar bo'ylab UZLUKSIZ davom etadi: ikkinchi
   * bo'limda yana 1 dan boshlansa, ekranda ikkita «1-chi» paydo
   * bo'lardi.
   */
  if (groups !== undefined) {
    let position = 0;
    return (
      <div
        /* Jadvalning `caption` i shu yerda qoladi: bo'limlar bilan
           ro'yxat bittadan ko'p bo'lib qoladi va ularni birlashtirib
           turgan nom ekran o'qigichga kerak. */
        {...(caption === undefined ? {} : { role: 'group', 'aria-label': caption })}
        className="flex flex-col gap-4"
      >
        {groups.map((group) => (
          <section key={group.key} className="flex flex-col gap-2.5">
            {group.header}
            {group.rows.length === 0 ? null : (
              <ul className="flex flex-col gap-2.5">
                {group.rows.map((row) => {
                  position += 1;
                  return cardOf(row, position, group.rowClassName);
                })}
              </ul>
            )}
          </section>
        ))}
      </div>
    );
  }

  return (
    <ul {...(caption === undefined ? {} : { 'aria-label': caption })} className="flex flex-col gap-2.5">
      {rows.map((row, index) => cardOf(row, index + 1))}
    </ul>
  );
};
